import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Odometer, OdometerUnit, Vehicle } from '@clearroadlab/cam';

import { environment } from '../../environments/environment';
import { UsersService } from '../users/users.service';
import { filterParams, handleAPIError, ISearchParams } from '../utils';

const path = `${environment.camApi.baseUrl}/odometers`;
const odometersPaths = `${environment.odometerApi.baseUrl}/api/odometers-readings`;
// Base auth path with provider placeholder that will be replaced dynamically
const authProviderBasePath = `${environment.authApi.baseUrl}/accounts`;

export interface IOdometerSearchParams extends ISearchParams {
  search?: string;
  searchIn?: string[];
  vin?: string;
  status?: string[];
}

export class OdometerSearchResult {
  count = 0;
  results: any[];
}

@Injectable({
  providedIn: 'root'
})
export class OdometersService {
  public odometer: Odometer;

  constructor(
    private http: HttpClient,
    private usersService: UsersService
  ) { }

  async get({ id, vin }: Partial<Odometer>) {
    return this.http.get<Odometer>(`${path}/${vin}/${id}`, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  public get authHeaders() {
    return new HttpHeaders({
      'x-access-token': environment.authApi.accessToken
    });
  }
  async value(vin: string) {
    return this.http.get<{ value: number; unit: OdometerUnit }>(`${path}/${vin}/value`, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  async list(params?: IOdometerSearchParams) {
    return this.http.get<OdometerSearchResult>(path, {
      headers: this.usersService.headers,
      params: filterParams(params)
    }).toPromise().catch(handleAPIError);
  }

  async delete({ id, vin }: Partial<Odometer>) {
    return this.http.delete(`${path}/${vin}/${id}`, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  async approve(odometer: any, data: any) {
    return this.http.put<Odometer>(`${path}/${odometer.relations.vehicle.vin}/${odometer.id}/approve`, data, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  async reject(odometer: any, data: any) {
    return this.http.put<Odometer>(`${path}/${odometer.relations.vehicle.vin}/${odometer.id}/fail`, data, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  async cancel(odometer: any, data: any) {
    return this.http.put<Odometer>(`${path}/${odometer.relations.vehicle.vin}/${odometer.id}/cancel`, data, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }
  async process(odometer: any) {
    return this.http.put<Odometer>(
      `${path}/${odometer?.relations?.vehicle?.vin}/${odometer.id}/reprocess`,
      {},
      {
        headers: this.usersService.headers
      }
    ).toPromise().catch(handleAPIError);
  }

  async update({ id, vin }: Partial<Odometer>, data: any) {
    return this.http.put<Odometer>(`${path}/${vin}/${id}`, data, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }


  async getVehicleOdometer(vin: string, email: string, deviceID: string , vehicle: Vehicle, wakeup: boolean = false) {
    let provider = 'smartcar';
    if (vehicle && vehicle.devices) {
      provider = vehicle.devices[vehicle.devices.length - 1].provider.toLowerCase();
      if (provider === 'tesla') {
        provider = 'tesla';
      } else {
        provider = 'smartcar';
      }
    }
    let url = `${authProviderBasePath}/${provider}/odometer/${vin}/?email=${encodeURIComponent(email)}&deviceId=${deviceID}`;
    if (wakeup) {
      url += '&wakeup=true&retryCount=3';
    }
    return this.http.get(url, {
    headers: this.authHeaders
    }).toPromise().catch(handleAPIError);
  }

}
